import { useCallback, useEffect, useState } from "react";

import { GlobalSearchItem } from "./GlobalSearch.types";

const MAX_RECENT_ITEMS = 5;
const STORAGE_KEY = "tournated:globalSearch:recent";

// Recent picks live in localStorage so they survive reloads but stay on this
// device. They're keyed by `${platformId}:${organizationId}` so each tenant
// scope keeps its own list — recents from one federation never bleed into
// another.
type StoredRecents = Record<string, GlobalSearchItem[]>;

// The header mounts more than one search instance at once (the desktop and
// mobile bars are both in the DOM, toggled only via CSS), so several
// `useRecentSearchItems` hooks share this one localStorage list. The browser
// `storage` event only fires in *other* tabs, so we keep an in-tab subscriber
// registry: after any write we ping every mounted instance to re-read the
// store. Without this, each instance keeps a divergent in-memory copy and the
// next write from a stale instance clobbers a freshly-recorded pick.
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

// `LicenseReference` and `TournamentSearchItem` carry `Date` fields that
// JSON.stringify flattens to ISO strings — revive them on read so views
// consuming recents see the same shape as fresh API results.
const reviveItem = (item: GlobalSearchItem): GlobalSearchItem => {
  if (item.type === "license") {
    return {
      ...item,
      license: {
        ...item.license,
        startDate: item.license.startDate ? new Date(item.license.startDate) : null,
        endDate: item.license.endDate ? new Date(item.license.endDate) : null,
      },
    };
  }
  if (item.type === "tournament") {
    return {
      ...item,
      startDate: item.startDate ? new Date(item.startDate) : null,
      endDate: item.endDate ? new Date(item.endDate) : null,
    };
  }
  return item;
};

const readStore = (): StoredRecents => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    const store = parsed as StoredRecents;
    return Object.fromEntries(
      Object.entries(store).map(([key, items]) => [key, items.map(reviveItem)])
    );
  } catch {
    return {};
  }
};

const writeStore = (store: StoredRecents) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded) — swallow.
  }
};

/**
 * Wipes the entire recent-search store across every tenant scope. Call on
 * logout / 401 so picks from the previous session can't leak into a new login
 * on the same browser.
 */
export const clearRecentSearchItems = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage may be unavailable — nothing to clear.
  }
  notifyListeners();
};

export type UseRecentSearchItemsReturn = {
  recentItems: GlobalSearchItem[];
  addRecentItem: (item: GlobalSearchItem) => void;
};

export const useRecentSearchItems = (scopeKey: string): UseRecentSearchItemsReturn => {
  const [recentItems, setRecentItems] = useState<GlobalSearchItem[]>(
    () => readStore()[scopeKey] ?? []
  );

  useEffect(() => {
    // Re-read on scope change and whenever any instance (this tab) or another
    // tab mutates the store, so every mounted search shows the same list.
    const sync = () => setRecentItems(readStore()[scopeKey] ?? []);
    sync();
    listeners.add(sync);
    window.addEventListener("storage", sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", sync);
    };
  }, [scopeKey]);

  const addRecentItem = useCallback(
    (item: GlobalSearchItem) => {
      // Derive the next list from the freshly-read store, not React state — a
      // sibling instance may have recorded a pick since we last rendered, and
      // building off stale state would drop it.
      const store = readStore();
      const prev = store[scopeKey] ?? [];
      const next = [item, ...prev.filter((existing) => existing.id !== item.id)].slice(
        0,
        MAX_RECENT_ITEMS
      );
      store[scopeKey] = next;
      writeStore(store);
      notifyListeners();
    },
    [scopeKey]
  );

  return { recentItems, addRecentItem };
};
